import { InputButton, MultilineTextInput } from '@infotech-soe/erp-ui-kit';
import { themes } from 'prism-react-renderer';
import React from 'react';

import { MultilineFormControlViewModel } from '../../../services/form/view-model/FormControlViewModel';
import { useAppSelector } from '../../../store/hooks';
import { isDarkTheme } from '../../../store/selectors/theme';

import { TFormControlConnectorProps } from './FormControlContainer';
import FormLabeledInputControl from './FormLabeledInputControl';
import FormTextControl from './hoc/FormTextControl';
import useExpandable from './useExpandable';
import { InputOnValidate } from './useForm';

interface Props extends Pick<TFormControlConnectorProps, 'areHintsShown'> {
  control: MultilineFormControlViewModel;
  value: string;
  onChange: (value: string) => void;
  onChanged: (value: string) => void;
  onSubmit: (value: unknown) => void;
  onValidate: InputOnValidate;
}

const FormMultilineTextInputControl: React.FC<Props> = ({
  areHintsShown,
  control,
  value,
  onChange,
  onChanged,
  onSubmit,
  onValidate,
}) => {
  const isDarkMode = useAppSelector(isDarkTheme);
  const {
    isExpanded,
    containerRef,
    toggleExpanded,
    buttonIcon,
    buttonTitle,
    renderExpanded,
  } = useExpandable({ enabled: control.expandable });

  const labeledControl = (
    <FormLabeledInputControl areHintsShown={areHintsShown} control={control}>
      <MultilineTextInput
        allowTextOverflow
        className="col"
        disabled={control.disabled}
        highlightLanguage={control.highlightLanguage}
        highlightTheme={isDarkMode ? themes.vsDark : themes.vsLight}
        invalid={control.invalid}
        lengthInBytes={control.lengthInBytes}
        maxLength={control.maxLength}
        name={control.name}
        readOnly={control.readonly}
        required={control.required}
        rows={control.rows}
        value={value}
        withFillProgress
        onChange={onChange}
        onChanged={onChanged}
        onSubmit={onSubmit}
        onValidate={onValidate}
      >
        {control.expandable && (
          <InputButton
            icon={buttonIcon}
            title={buttonTitle}
            onClick={toggleExpanded}
          />
        )}
      </MultilineTextInput>
    </FormLabeledInputControl>
  );

  if (isExpanded) {
    return renderExpanded(labeledControl);
  }

  return control.expandable ? (
    <div ref={containerRef} style={{ display: 'contents' }}>
      {labeledControl}
    </div>
  ) : (
    labeledControl
  );
};

export default FormTextControl(FormMultilineTextInputControl);
